<script setup lang="ts">
import { computed, reactive, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { ArrowRight, Check, CircleCheck, Info, KeyRound, Plus, Sparkles, X } from "@lucide/vue";
import Combobox from "@/components/ui/Combobox.vue";
import ImportSection from "./ImportSection.vue";
import { normalize } from "./columnMatching";
import type { ColumnMatchingValue, FileColumn, TargetField } from "./types";

const props = withDefaults(defineProps<{
  columns: FileColumn[];
  fields: TargetField[];
  /** Seeds the matching on mount; the matching made in the component is then emitted back. */
  modelValue?: ColumnMatchingValue;
  /** Field the import recognizes existing records by. */
  identifierKey?: string;
  /** Fields cannot be chosen yet: neither pruned nor suggested. */
  disabled?: boolean;
  prefilled?: boolean;
}>(), {
  modelValue: () => ({}),
  identifierKey: undefined,
  disabled: false,
  prefilled: false,
});

const emit = defineEmits<{
  (e: "update:modelValue", value: ColumnMatchingValue): void;
}>();

const { t } = useI18n();

/** File column → keys of the fields it is imported into; null is a slot still to be chosen. */
const assignments = reactive<Record<string, (string | null)[]>>(
  Object.fromEntries(props.columns.map(column => [column.id, []]))
);
const suggestedColumns = ref<Set<string>>(new Set());

Object.entries(props.modelValue).forEach(([key, column]) => {
  if (assignments[column] && !assignments[column].includes(key)) {
    assignments[column].push(key);
  }
});

const columnByField = computed<ColumnMatchingValue>(() => {
  const map: ColumnMatchingValue = {};
  Object.entries(assignments).forEach(([column, keys]) => keys.forEach((key) => {
    if (key) map[key] = column;
  }));
  return map;
});

const fieldByKey = computed(() => Object.fromEntries(props.fields.map(field => [field.key, field])));

/** Only fields still offered are sent: one gone with the import settings stays matched until it is pruned. */
const matching = computed<ColumnMatchingValue>(() =>
  Object.fromEntries(Object.entries(columnByField.value).filter(([key]) => key in fieldByKey.value))
);

watch(matching, value => emit("update:modelValue", value), { immediate: true });

const suggestMatches = (): void => {
  props.columns.forEach((column) => {
    if (assignments[column.id].some(Boolean)) return;

    const name = normalize(column.id);
    const field = props.fields.find(
      candidate => !columnByField.value[candidate.key] && candidate.aliases.includes(name)
    );

    if (field) {
      assignments[column.id] = [field.key];
      suggestedColumns.value.add(column.id);
    }
  });
};

// A field that disappears with the import settings cannot stay matched: it would be sent without
// being shown. Columns it frees are then offered the fields the new settings bring.
watch(() => [props.disabled, props.fields.map(field => field.key).join()], () => {
  if (props.disabled) return;

  Object.keys(assignments).forEach((column) => {
    assignments[column] = assignments[column].filter(key => key === null || key in fieldByKey.value);
    if (assignments[column].length === 0) suggestedColumns.value.delete(column);
  });
  suggestMatches();
}, { immediate: true });

const slotsFor = (column: string): (string | null)[] =>
  assignments[column].length > 0 ? assignments[column] : [null];

const setAssignment = (column: string, index: number, key: string | undefined): void => {
  const list = assignments[column];
  if ((list[index] ?? null) === (key ?? null)) return;

  if (key === undefined) {
    list.splice(index, 1);
  } else {
    list[index] = key;
  }
  suggestedColumns.value.delete(column);
};

const removeSlot = (column: string, index: number): void => {
  assignments[column].splice(index, 1);
};

const optionsFor = (current: string | null) =>
  props.fields.map(field => ({
    value: field.key,
    label: field.label,
    description: field.key !== current && field.key in columnByField.value
      ? t("Already imported from {column}", { column: columnByField.value[field.key] })
      : field.group,
    disabled: field.key !== current && field.key in columnByField.value,
  }));

const importedKeys = (column: string): string[] =>
  assignments[column].filter((key): key is string => key !== null && key in fieldByKey.value);

const columnStatus = (column: string): "identifier" | "suggested" | "matched" | "skipped" => {
  const keys = importedKeys(column);
  if (keys.length === 0) return "skipped";
  if (props.identifierKey && keys.includes(props.identifierKey)) return "identifier";
  return suggestedColumns.value.has(column) ? "suggested" : "matched";
};

const importedColumnsCount = computed(() => props.columns.filter(column => importedKeys(column.id).length > 0).length);
const suggestionsCount = computed(() => props.columns.filter(column => columnStatus(column.id) === "suggested").length);

const sampleValue = (column: FileColumn): string =>
  column.example === null || column.example === undefined ? "" : String(column.example);
</script>

<template>
  <ImportSection
    :title="$t('Match your columns')"
    :description="$t('Pick the field each column of your file goes into. Columns left empty are not imported.')"
  >
    <template #aside>
      <span class="tw:shrink-0 tw:text-sm tw:text-zinc-500">
        {{ $t("{count} of {total} columns imported", { count: importedColumnsCount, total: columns.length }) }}
      </span>
    </template>

    <div v-if="!disabled && (prefilled || suggestionsCount > 0)" class="tw:flex tw:flex-wrap tw:gap-2 tw:px-5 tw:pt-4">
      <span v-if="prefilled" class="tw:inline-flex tw:items-center tw:gap-1.5 tw:rounded-md tw:bg-zinc-100 tw:px-2 tw:py-1 tw:text-xs tw:text-zinc-700">
        <CircleCheck class="tw:size-3.5" />
        {{ $t("Pre-filled with the settings of your last import") }}
      </span>
      <span v-if="suggestionsCount > 0" class="tw:inline-flex tw:items-center tw:gap-1.5 tw:rounded-md tw:bg-sky-50 tw:px-2 tw:py-1 tw:text-xs tw:text-sky-700">
        <Sparkles class="tw:size-3.5" />
        {{ $t("{count} columns matched from their name, check them before importing", { count: suggestionsCount }) }}
      </span>
    </div>

    <div v-if="disabled && $slots.disabled" class="tw:mx-5 tw:mt-4 tw:flex tw:items-center tw:gap-2 tw:rounded-lg tw:bg-zinc-50 tw:px-3 tw:py-2 tw:text-sm tw:text-zinc-600">
      <Info class="tw:size-4 tw:shrink-0" />
      <slot name="disabled" />
    </div>

    <div class="tw:px-5 tw:py-3">
      <div class="tw:hidden tw:md:grid tw:grid-cols-[minmax(0,1fr)_1.5rem_minmax(0,1.2fr)_7.5rem] tw:gap-4 tw:pb-2 tw:text-xs tw:font-medium tw:uppercase tw:tracking-wide tw:text-zinc-500">
        <span>{{ $t("Column in your file") }}</span>
        <span></span>
        <span>{{ $t("Imports as") }}</span>
        <span class="tw:text-right">{{ $t("Status") }}</span>
      </div>

      <div
        v-for="column in columns"
        :key="column.id"
        class="tw:grid tw:grid-cols-1 tw:md:grid-cols-[minmax(0,1fr)_1.5rem_minmax(0,1.2fr)_7.5rem] tw:items-start tw:gap-x-4 tw:gap-y-2 tw:border-t tw:border-zinc-100 tw:py-3"
      >
        <div class="tw:min-w-0 tw:pt-1.5" :class="{ 'tw:opacity-60': columnStatus(column.id) === 'skipped' }">
          <div class="tw:truncate tw:font-mono tw:text-sm" :title="column.label">{{ column.label }}</div>
          <div class="tw:truncate tw:text-sm tw:text-zinc-500" :title="sampleValue(column)">
            <template v-if="sampleValue(column)">{{ sampleValue(column) }}</template>
            <span v-else class="tw:italic tw:text-zinc-400">{{ $t("Empty in the first row") }}</span>
          </div>
        </div>

        <ArrowRight class="tw:hidden tw:md:block tw:mt-2.5 tw:size-4 tw:text-zinc-400" />

        <div class="tw:min-w-0 tw:space-y-2">
          <div v-for="(key, index) in slotsFor(column.id)" :key="index" class="tw:flex tw:items-center tw:gap-2">
            <Combobox
              class="tw:min-w-0 tw:flex-1"
              :model-value="key ?? undefined"
              :options="optionsFor(key)"
              :placeholder="$t('Don’t import')"
              :disabled="disabled"
              :fullwidth="true"
              :virtualize="false"
              @update:model-value="(value) => setAssignment(column.id, index, value)"
            />
            <button
              v-if="index > 0"
              type="button"
              class="tw:shrink-0 tw:rounded-md tw:p-1.5 tw:text-zinc-400 tw:hover:bg-zinc-100 tw:hover:text-zinc-700"
              :aria-label="$t('Remove')"
              @click="removeSlot(column.id, index)"
            >
              <X class="tw:size-4" />
            </button>
            <button
              v-else-if="assignments[column.id].length > 0 && !assignments[column.id].includes(null)"
              type="button"
              class="tw:shrink-0 tw:rounded-md tw:p-1.5 tw:text-zinc-400 tw:hover:bg-zinc-100 tw:hover:text-zinc-700"
              :aria-label="$t('Also import into another field')"
              :title="$t('Also import into another field')"
              @click="assignments[column.id].push(null)"
            >
              <Plus class="tw:size-4" />
            </button>
            <span v-else aria-hidden="true" class="tw:size-7 tw:shrink-0"></span>
          </div>
        </div>

        <div class="tw:md:pt-2 tw:md:text-right tw:text-sm">
          <span v-if="columnStatus(column.id) === 'identifier'" class="tw:inline-flex tw:items-center tw:gap-1.5 tw:font-medium tw:text-zinc-900">
            <KeyRound class="tw:size-4" /> {{ $t("Identifier") }}
          </span>
          <span v-else-if="columnStatus(column.id) === 'suggested'" class="tw:inline-flex tw:items-center tw:gap-1.5 tw:font-medium tw:text-sky-700" :title="$t('Matched from the column name')">
            <Sparkles class="tw:size-4" /> {{ $t("Suggested") }}
          </span>
          <span v-else-if="columnStatus(column.id) === 'matched'" class="tw:inline-flex tw:items-center tw:gap-1.5 tw:font-medium tw:text-green-700">
            <Check class="tw:size-4" /> {{ $t("Matched") }}
          </span>
          <span v-else class="tw:text-zinc-400">{{ $t("Not imported") }}</span>
        </div>
      </div>
    </div>

    <template v-for="field in fields" :key="field.key">
      <input type="hidden" :name="field.inputName" :value="columnByField[field.key] ?? ''">
    </template>
  </ImportSection>
</template>
